import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Stack,
  Box,
  Tooltip,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  AccountCircle as AccountCircleIcon,
  Assignment as AssignmentIcon,
  Speed as SpeedIcon,
  Update as UpdateIcon
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { useNotificationsStore } from '../store/notificationsStore';
import { useUsuarioStore } from '../store/usuarioStore';
import NotificationHeader from './Alert/NotificationHeader';
import ScraperInfoModal from './ScraperInfo/ScraperInfoModal';
import { useNavigate, useLocation } from 'react-router-dom';

const Header: React.FC = () => {
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [scraperInfoModalOpen, setScraperInfoModalOpen] = useState(false);
  const { getUnreadCount } = useNotificationsStore();
  const { logout } = useAuth0();
  const { usuario } = useUsuarioStore();
  const unreadCount = getUnreadCount();
  const navigate = useNavigate();
  const location = useLocation();

  const getIconColor = (path: string) => {
    return location.pathname === path ? '#1976D2' : '#585858';
  };

  const handleNotificationClick = () => {
    setNotificationDrawerOpen(true);
  };

  const handleNotificationDrawerClose = () => {
    setNotificationDrawerOpen(false);
  };

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  const handleUserClick = () => {
    navigate('/user');
  };

  const handleLicitacionesClick = () => {
    navigate('/');
  };

  const handleComprasAgilesClick = () => {
    navigate('/compras-agiles');
  };

  const getLogoByTematica = (nombreTematica: string | undefined | null): string => {
    if (!nombreTematica) {
      return '/logo.png';
    }
    
    const tematicaLower = nombreTematica.toLowerCase();
    
    if (tematicaLower === 'mecánica' || tematicaLower === 'mecanica' || 
        tematicaLower === 'bombas de agua' || tematicaLower === 'climatización' || tematicaLower === 'climatizacion') {
      return '/globalmec.png';
    }
    
    if (tematicaLower === 'estructural' || tematicaLower === 'pavimentación' || tematicaLower === 'pavimentacion') {
      return '/EMINSUR.png';
    }
    
    return '/logo.png';
  };

  return (
    <>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          backgroundColor: 'white',
          borderBottom: '1px solid #e0e0e0',
          height: '70px',
          justifyContent: 'center'
        }}
      >
        <Toolbar sx={{ height: '70px', px: { xs: 2, md: 3 } }}>
          <Box 
            sx={{ 
              flexGrow: 1,
              '@media (min-width: 1900px)': {
                paddingLeft: '3rem',
              }
            }}
          >
            <Box
              sx={{
                height: { xs: '28px', sm: '40px' },
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <img 
                src={getLogoByTematica(usuario?.tematica?.nombre)} 
                alt="Glaciar Ingeniería" 
                style={{ 
                  height: '100%',
                  width: 'auto',
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/')}
              />
            </Box>
          </Box>

          <Stack 
            direction="row" 
            spacing={1} 
            alignItems="center"
            sx={{
              '@media (min-width: 1900px)': {
                paddingRight: '3rem',
              }
            }}
          >
            {usuario?.tematica?.nombre && (
              <Tooltip title={usuario.tematica.nombre} arrow>
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    maxWidth: '120px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                    color: '#1976D2',
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  {usuario.tematica.nombre}
                </Box>
              </Tooltip>
            )}

            <Tooltip title="Última ejecución del scraper" arrow>
              <IconButton
                sx={{ color: '#585858' }}
                onClick={() => setScraperInfoModalOpen(true)}
              >
                <UpdateIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Licitaciones" arrow>
              <IconButton
                sx={{ color: getIconColor('/') }}
                onClick={handleLicitacionesClick}
              >
                <AssignmentIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Compras Ágiles" arrow>
              <IconButton
                sx={{ color: getIconColor('/compras-agiles') }}
                onClick={handleComprasAgilesClick}
              >
                <SpeedIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Notificaciones" arrow>
              <IconButton
                sx={{ color: getIconColor('/alerts') }}
                onClick={handleNotificationClick}
              >
                <Badge badgeContent={unreadCount} color="primary">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={!usuario ? "Cargando..." : usuario.nombreCompleto || "Usuario"} arrow>
              <IconButton
                sx={{ color: getIconColor('/user') }}
                onClick={handleUserClick}
              >
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Cerrar sesión" arrow>
              <IconButton
                sx={{ color: '#585858' }}
                onClick={handleLogout}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      <NotificationHeader 
        open={notificationDrawerOpen}
        onClose={handleNotificationDrawerClose}
      />

      <ScraperInfoModal
        open={scraperInfoModalOpen}
        onClose={() => setScraperInfoModalOpen(false)}
      />
    </>
  );
};

export default Header;